import type { Deal, Company, Contact, Task } from "../../types";
import type {
  AdvancedFilter,
  FilterCondition,
  FilterOperator,
} from "./filterTypes";

export interface FilterEvaluationContext {
  companiesMap?: Record<string, Company>;
  contactsMap?: Record<string, Contact>;
  tasksByDealMap?: Record<string, Task[]>;
  salesUsersMap?: Record<string, { id: string | number; name: string }>;
  dealHistoryMap?: Record<string, any[]>;
}

export function normalizeStatus(val: any): string {
  if (val === null || val === undefined) return "";
  const s = String(val).toLowerCase().trim();
  if (s === "won" || s === "ganho") return "ganho";
  if (s === "lost" || s === "perdido") return "perdido";
  if (s === "open" || s === "aberto" || s === "em aberto") return "aberto";
  return s;
}

export function normalizeTaskType(val: any): string {
  if (val === null || val === undefined) return "";
  const s = String(val).toLowerCase().trim();
  if (s === "ligação" || s === "ligacao" || s === "call") return "call";
  if (s === "e-mail" || s === "email") return "email";
  if (s === "reunião" || s === "reuniao" || s === "meeting") return "meeting";
  if (s === "lembrete" || s === "reminder") return "reminder";
  if (s === "almoço" || s === "almoco" || s === "lunch") return "lunch";
  if (s === "outro" || s === "other") return "other";
  return s;
}

function parseDateParts(val: any): {
  localYmd: string;
  utcYmd: string;
  rawPrefix: string;
  timeMs: number;
} | null {
  if (val === null || val === undefined || val === "") return null;
  let str = String(val).trim();

  // Se estiver no formato brasileiro DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}/.test(str)) {
    const parts = str.split("/");
    str = `${parts[2]}-${parts[1]}-${parts[0]}${str.slice(10)}`;
  }

  const rawPrefix = /^\d{4}-\d{2}-\d{2}/.test(str) ? str.slice(0, 10) : "";
  const dateObj = new Date(str);

  if (isNaN(dateObj.getTime())) {
    if (rawPrefix) {
      return { localYmd: rawPrefix, utcYmd: rawPrefix, rawPrefix, timeMs: 0 };
    }
    return null;
  }

  const localYmd = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;
  let utcYmd = "";
  try {
    utcYmd = dateObj.toISOString().slice(0, 10);
  } catch {
    utcYmd = localYmd;
  }

  return {
    localYmd,
    utcYmd: rawPrefix || utcYmd,
    rawPrefix,
    timeMs: dateObj.getTime(),
  };
}

function evaluateDateOperator(
  recordValue: any,
  operator: FilterOperator,
  targetValue: any,
): boolean {
  const rec = parseDateParts(recordValue);
  const tgt = parseDateParts(targetValue);

  if (!tgt) return false;
  if (!rec) {
    return operator === "não é";
  }

  const targetYmd = tgt.rawPrefix || tgt.localYmd;
  const isSameDay =
    rec.localYmd === targetYmd ||
    rec.utcYmd === targetYmd ||
    rec.rawPrefix === targetYmd;

  switch (operator) {
    case "é":
      return isSameDay;
    case "não é":
      return !isSameDay;
    case "antes de":
      return !isSameDay && (rec.localYmd < targetYmd || rec.utcYmd < targetYmd);
    case "depois de":
      return !isSameDay && (rec.localYmd > targetYmd || rec.utcYmd > targetYmd);
    case "é exatamente ou antes de":
      return isSameDay || rec.localYmd <= targetYmd || rec.utcYmd <= targetYmd;
    case "é exatamente ou depois de":
      return isSameDay || rec.localYmd >= targetYmd || rec.utcYmd >= targetYmd;
    default:
      return false;
  }
}

function parseTimeSeconds(val: any): number | null {
  if (!val || typeof val !== "string") return null;
  const match = val.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = match[3] ? parseInt(match[3], 10) : 0;
  return hours * 3600 + minutes * 60 + seconds;
}

function evaluateTimeOperator(
  recordValue: any,
  operator: FilterOperator,
  targetValue: any,
): boolean {
  const recSec = parseTimeSeconds(recordValue);
  const tgtSec = parseTimeSeconds(targetValue);

  if (tgtSec === null) return false;
  if (recSec === null) return operator === "não é";

  switch (operator) {
    case "é":
      return recSec === tgtSec;
    case "não é":
      return recSec !== tgtSec;
    case "antes de":
      return recSec < tgtSec;
    case "depois de":
      return recSec > tgtSec;
    case "é exatamente ou antes de":
      return recSec <= tgtSec;
    case "é exatamente ou depois de":
      return recSec >= tgtSec;
    default:
      return false;
  }
}

function parseNumber(val: any): number {
  if (typeof val === "number") return val;
  if (val === null || val === undefined || val === "") return NaN;
  const cleaned = String(val)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const num = Number(cleaned);
  return isNaN(num) ? Number(val) : num;
}

export function evaluateOperator(
  recordValue: any,
  operator: FilterOperator,
  targetValue: any,
): boolean {
  // Operadores de vazio/não vazio
  if (operator === "esta vazio") {
    return (
      recordValue === undefined ||
      recordValue === null ||
      (typeof recordValue === "string" && recordValue.trim() === "") ||
      (Array.isArray(recordValue) && recordValue.length === 0)
    );
  }

  if (operator === "não esta vazio") {
    return (
      recordValue !== undefined &&
      recordValue !== null &&
      (typeof recordValue !== "string" || recordValue.trim() !== "") &&
      (!Array.isArray(recordValue) || recordValue.length > 0)
    );
  }

  // Se for comparação de data
  const isDateOp =
    operator === "antes de" ||
    operator === "depois de" ||
    operator === "é exatamente ou antes de" ||
    operator === "é exatamente ou depois de";

  const isTargetDate =
    typeof targetValue === "string" &&
    (/^\d{4}-\d{2}-\d{2}/.test(targetValue.trim()) ||
      /^\d{2}\/\d{2}\/\d{4}/.test(targetValue.trim()));

  const isRecordDate =
    typeof recordValue === "string" &&
    /^\d{4}-\d{2}-\d{2}/.test(recordValue.trim());

  if (
    isDateOp ||
    (isTargetDate && (operator === "é" || operator === "não é")) ||
    (isRecordDate && isTargetDate)
  ) {
    return evaluateDateOperator(recordValue, operator, targetValue);
  }

  // Se for comparação de hora (ex: "16:41")
  const isTargetTime =
    typeof targetValue === "string" &&
    /^\d{1,2}:\d{2}/.test(targetValue.trim());
  if (isTargetTime && (isDateOp || operator === "é" || operator === "não é")) {
    return evaluateTimeOperator(recordValue, operator, targetValue);
  }

  // Tratamento de strings padrão
  const valStr =
    recordValue === null || recordValue === undefined
      ? ""
      : String(recordValue).toLowerCase().trim();

  const targetStr =
    targetValue === null || targetValue === undefined
      ? ""
      : String(targetValue).toLowerCase().trim();

  switch (operator) {
    // Texto / Autocompletar / Endereço / Escolha
    case "é":
    case "mudou para":
      return valStr === targetStr;

    case "não é":
      return valStr !== targetStr;

    case "contem":
      return valStr.includes(targetStr);

    case "não contem":
      return !valStr.includes(targetStr);

    case "começa com":
      return valStr.startsWith(targetStr);

    case "não começa com":
      return !valStr.startsWith(targetStr);

    case "termina com":
      return valStr.endsWith(targetStr);

    case "não termina com":
      return !valStr.endsWith(targetStr);

    // Múltipla escolha / Opção única
    case "contem algum de": {
      const targets = Array.isArray(targetValue)
        ? targetValue.map((v) => String(v).toLowerCase().trim())
        : [targetStr];
      if (Array.isArray(recordValue)) {
        const arr = recordValue.map((v) => String(v).toLowerCase().trim());
        return targets.some((t) => arr.includes(t));
      }
      return targets.some((t) => valStr.includes(t));
    }

    case "não contem nem um de": {
      const targets = Array.isArray(targetValue)
        ? targetValue.map((v) => String(v).toLowerCase().trim())
        : [targetStr];
      if (Array.isArray(recordValue)) {
        const arr = recordValue.map((v) => String(v).toLowerCase().trim());
        return !targets.some((t) => arr.includes(t));
      }
      return !targets.some((t) => valStr.includes(t));
    }

    // Número e Monetário
    case "=": {
      const numVal = parseNumber(recordValue);
      const numTarget = parseNumber(targetValue);
      if (isNaN(numVal) || isNaN(numTarget)) return valStr === targetStr;
      return numVal === numTarget;
    }

    case "≠": {
      const numVal = parseNumber(recordValue);
      const numTarget = parseNumber(targetValue);
      if (isNaN(numVal) || isNaN(numTarget)) return valStr !== targetStr;
      return numVal !== numTarget;
    }

    case "<": {
      const numVal = parseNumber(recordValue);
      const numTarget = parseNumber(targetValue);
      return !isNaN(numVal) && !isNaN(numTarget) && numVal < numTarget;
    }

    case ">": {
      const numVal = parseNumber(recordValue);
      const numTarget = parseNumber(targetValue);
      return !isNaN(numVal) && !isNaN(numTarget) && numVal > numTarget;
    }

    case "≤": {
      const numVal = parseNumber(recordValue);
      const numTarget = parseNumber(targetValue);
      return !isNaN(numVal) && !isNaN(numTarget) && numVal <= numTarget;
    }

    case "≥": {
      const numVal = parseNumber(recordValue);
      const numTarget = parseNumber(targetValue);
      return !isNaN(numVal) && !isNaN(numTarget) && numVal >= numTarget;
    }

    default:
      return true;
  }
}

function getDealHistory(
  recordId: any,
  context?: FilterEvaluationContext,
): any[] {
  if (context?.dealHistoryMap && context.dealHistoryMap[String(recordId)]) {
    return context.dealHistoryMap[String(recordId)];
  }
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const raw = window.localStorage.getItem("crm.deal_history_map");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed[String(recordId)]) return parsed[String(recordId)];
      }
    }
  } catch {
    void 0;
  }
  return [];
}

export function extractFieldValue(
  record: any,
  fieldId: string,
  context?: FilterEvaluationContext,
): any {
  if (!record) return undefined;

  // 1. Verificar em custom_fields se existir
  if (record.custom_fields) {
    const cleanId = fieldId.replace(/^(cf_)+/, "");
    if (record.custom_fields[fieldId] !== undefined) {
      return record.custom_fields[fieldId];
    }
    if (record.custom_fields[`cf_${cleanId}`] !== undefined) {
      return record.custom_fields[`cf_${cleanId}`];
    }
    if (record.custom_fields[cleanId] !== undefined) {
      return record.custom_fields[cleanId];
    }
    if (record.custom_fields[`cf_cf_${cleanId}`] !== undefined) {
      return record.custom_fields[`cf_cf_${cleanId}`];
    }
  }

  // 2. Mapeamentos especiais do Deal
  if (fieldId === "status") {
    return normalizeStatus(
      record.status ||
        (record.stage === "won"
          ? "ganho"
          : record.stage === "lost"
            ? "perdido"
            : "aberto"),
    );
  }
  if (
    fieldId === "funil_id" ||
    fieldId === "pipeline_id" ||
    fieldId === "funil"
  ) {
    return String(record.funil_id || record.pipeline_id || "principal");
  }
  if (fieldId === "stage") {
    return record.stage_id || record.stage;
  }
  if (fieldId === "amount" || fieldId === "valor") {
    return record.valor_proposta !== undefined && record.valor_proposta !== null
      ? record.valor_proposta
      : record.amount !== undefined && record.amount !== null
        ? record.amount
        : 0;
  }
  if (fieldId === "temperatura") {
    return record.temperatura || "";
  }
  if (fieldId === "canal_origem" || fieldId === "origem") {
    return record.origem || record.canal_origem || "";
  }
  if (fieldId === "category") {
    return record.category || "";
  }
  if (fieldId === "sales_id") {
    return record.sales_id !== undefined && record.sales_id !== null
      ? String(record.sales_id)
      : "";
  }
  if (fieldId === "company_id") {
    const company = context?.companiesMap?.[String(record.company_id)];
    return (
      company?.name ||
      (record.company_id !== undefined && record.company_id !== null
        ? String(record.company_id)
        : "")
    );
  }
  if (fieldId === "contact_id") {
    const contact = record.contact_ids?.[0]
      ? context?.contactsMap?.[String(record.contact_ids[0])]
      : undefined;
    return contact ? `${contact.first_name} ${contact.last_name}`.trim() : "";
  }
  if (fieldId === "done_activities") return record.nb_tasks_done || 0;
  if (fieldId === "pending_activities") return record.nb_tasks_pending || 0;
  if (fieldId === "total_activities") return record.nb_tasks || 0;

  // 3. Mapeamentos de datas e status baseados no negócio e histórico
  if (fieldId === "won_at" || fieldId === "ganho_em") {
    if (record.won_at) return record.won_at;
    if (record.ganho_em) return record.ganho_em;

    const history = getDealHistory(record.id, context);
    if (history.length > 0) {
      const wonEvent = history.find(
        (h) =>
          (h.type === "status_change" &&
            (h.new_value === "ganho" || h.new_value === "won")) ||
          (h.field_name === "status" &&
            (h.new_value === "ganho" || h.new_value === "won")) ||
          h.label?.toLowerCase().includes("ganho") ||
          h.description?.toLowerCase().includes("ganho"),
      );
      if (wonEvent?.created_at) return wonEvent.created_at;
    }

    const st = normalizeStatus(
      record.status || (record.stage === "won" ? "ganho" : ""),
    );
    if (st === "ganho" || record.stage === "won") {
      return record.updated_at || record.created_at;
    }
    return undefined;
  }

  if (fieldId === "lost_at" || fieldId === "perdido_em") {
    if (record.lost_at) return record.lost_at;
    if (record.perdido_em) return record.perdido_em;

    const history = getDealHistory(record.id, context);
    if (history.length > 0) {
      const lostEvent = history.find(
        (h) =>
          (h.type === "status_change" &&
            (h.new_value === "perdido" || h.new_value === "lost")) ||
          (h.field_name === "status" &&
            (h.new_value === "perdido" || h.new_value === "lost")) ||
          h.label?.toLowerCase().includes("perdido") ||
          h.description?.toLowerCase().includes("perdido"),
      );
      if (lostEvent?.created_at) return lostEvent.created_at;
    }

    const st = normalizeStatus(
      record.status || (record.stage === "lost" ? "perdido" : ""),
    );
    if (st === "perdido" || record.stage === "lost") {
      return record.updated_at || record.created_at;
    }
    return undefined;
  }

  if (fieldId === "closed_at" || fieldId === "fechado_em") {
    if (record.closed_at) return record.closed_at;
    const st = normalizeStatus(
      record.status ||
        (record.stage === "won"
          ? "ganho"
          : record.stage === "lost"
            ? "perdido"
            : ""),
    );
    if (st === "ganho" || record.stage === "won") {
      return extractFieldValue(record, "won_at", context);
    }
    if (st === "perdido" || record.stage === "lost") {
      return extractFieldValue(record, "lost_at", context);
    }
    return undefined;
  }

  if (fieldId === "stage_entered_at" || fieldId === "stage_changed_at") {
    if (record[fieldId]) return record[fieldId];
    const history = getDealHistory(record.id, context);
    if (history.length > 0) {
      const stageEvent = history.find((h) => h.type === "stage_change");
      if (stageEvent?.created_at) return stageEvent.created_at;
    }
    return record.updated_at || record.created_at;
  }

  if (fieldId === "last_activity_date") {
    if (record.last_activity_date) return record.last_activity_date;
    const dealTasks = context?.tasksByDealMap?.[String(record.id)];
    if (dealTasks && dealTasks.length > 0) {
      const dates = dealTasks
        .map((t: any) => t.done_date || t.due_date || t.created_at)
        .filter(Boolean)
        .sort();
      if (dates.length > 0) return dates[dates.length - 1];
    }
    return undefined;
  }

  if (fieldId === "next_activity_date") {
    if (record.next_activity_date) return record.next_activity_date;
    const dealTasks = context?.tasksByDealMap?.[String(record.id)];
    if (dealTasks && dealTasks.length > 0) {
      const pendingDates = dealTasks
        .filter((t: any) => !t.done_date)
        .map((t: any) => t.due_date)
        .filter(Boolean)
        .sort();
      if (pendingDates.length > 0) return pendingDates[0];
    }
    return undefined;
  }

  if (fieldId === "created_by") {
    return record.created_by || record.sales_id || "";
  }

  if (fieldId === "motivo_perda") {
    return record.motivo_perda || "";
  }

  // 4. Campo direto
  return record[fieldId];
}

export function evaluateConditionForDeal(
  deal: Deal,
  condition: FilterCondition,
  context?: FilterEvaluationContext,
): boolean {
  if (!condition.fieldId) return true;

  // Se a condição for diretamente sobre o Negócio
  if (condition.entity === "deal") {
    const targetVal = condition.value;

    // Tratamento especial para o operador "mudou para"
    if (condition.operator === "mudou para") {
      const history = getDealHistory(deal.id, context);
      const targetNormalized =
        condition.fieldId === "status"
          ? normalizeStatus(targetVal)
          : String(targetVal ?? "")
              .toLowerCase()
              .trim();

      // 1. Checa eventos no histórico de auditoria
      if (history && history.length > 0) {
        const hasHistoryMatch = history.some((evt: any) => {
          if (condition.fieldId === "stage" && evt.type === "stage_change") {
            const newV = String(evt.new_value ?? "")
              .toLowerCase()
              .trim();
            return (
              newV === targetNormalized ||
              newV.includes(targetNormalized) ||
              targetNormalized.includes(newV)
            );
          }
          if (
            condition.fieldId === "status" &&
            (evt.type === "status_change" || evt.field_name === "status")
          ) {
            return normalizeStatus(evt.new_value) === targetNormalized;
          }
          if (
            condition.fieldId === "sales_id" &&
            (evt.type === "owner_change" || evt.field_name === "sales_id")
          ) {
            const newV = String(evt.new_value ?? "")
              .toLowerCase()
              .trim();
            return (
              newV === targetNormalized ||
              String(evt.new_value) === String(targetVal)
            );
          }
          if (
            evt.field_name === condition.fieldId ||
            evt.type === "field_change" ||
            evt.type === "custom_field_change"
          ) {
            const newV = String(evt.new_value ?? "")
              .toLowerCase()
              .trim();
            return newV === targetNormalized;
          }
          return false;
        });

        if (hasHistoryMatch) return true;
      }

      // 2. Se o valor atual é igual ao valor alvo
      const rawVal = extractFieldValue(deal, condition.fieldId, context);
      if (condition.fieldId === "status") {
        return normalizeStatus(rawVal) === targetNormalized;
      }
      if (condition.fieldId === "stage") {
        const curStage = String(rawVal ?? "")
          .toLowerCase()
          .trim();
        return (
          curStage === targetNormalized ||
          curStage.includes(targetNormalized) ||
          targetNormalized.includes(curStage)
        );
      }
      return (
        String(rawVal ?? "")
          .toLowerCase()
          .trim() === targetNormalized
      );
    }

    const rawVal = extractFieldValue(deal, condition.fieldId, context);

    // Normalização para status (open <-> aberto, won <-> ganho, lost <-> perdido)
    if (condition.fieldId === "status") {
      const normalizedRaw = normalizeStatus(rawVal);
      const normalizedTarget = Array.isArray(targetVal)
        ? targetVal.map(normalizeStatus)
        : normalizeStatus(targetVal);
      return evaluateOperator(
        normalizedRaw,
        condition.operator,
        normalizedTarget,
      );
    }

    // Normalização para stage (slug vs label)
    if (condition.fieldId === "stage") {
      const curStage = String(rawVal ?? "")
        .toLowerCase()
        .trim();
      const tgtStage = String(targetVal ?? "")
        .toLowerCase()
        .trim();
      if (condition.operator === "é") {
        return (
          curStage === tgtStage ||
          curStage.includes(tgtStage) ||
          tgtStage.includes(curStage)
        );
      }
      if (condition.operator === "não é") {
        return !(
          curStage === tgtStage ||
          curStage.includes(tgtStage) ||
          tgtStage.includes(curStage)
        );
      }
    }

    return evaluateOperator(rawVal, condition.operator, condition.value);
  }

  // Se a condição for sobre a Organização vinculada
  if (condition.entity === "company") {
    if (!deal.company_id) {
      return condition.operator === "esta vazio";
    }
    const company = context?.companiesMap?.[String(deal.company_id)];
    if (!company) {
      return true;
    }
    const rawVal = extractFieldValue(company, condition.fieldId);
    return evaluateOperator(rawVal, condition.operator, condition.value);
  }

  // Se a condição for sobre a Pessoa / Contato vinculado
  if (condition.entity === "contact") {
    if (!deal.contact_ids || deal.contact_ids.length === 0) {
      return condition.operator === "esta vazio";
    }
    const matchingContacts = deal.contact_ids
      .map((id) => context?.contactsMap?.[String(id)])
      .filter(Boolean);

    if (matchingContacts.length === 0) return true;

    return matchingContacts.some((contact) => {
      const rawVal = extractFieldValue(contact, condition.fieldId);
      return evaluateOperator(rawVal, condition.operator, condition.value);
    });
  }

  // Se a condição for sobre Atividades vinculadas
  if (condition.entity === "activity") {
    const dealTasks = context?.tasksByDealMap?.[String(deal.id)] || [];
    if (dealTasks.length === 0) {
      return condition.operator === "esta vazio";
    }
    return dealTasks.some((task) => {
      const rawVal = extractFieldValue(task, condition.fieldId);
      if (condition.fieldId === "type") {
        return evaluateOperator(
          normalizeTaskType(rawVal),
          condition.operator,
          normalizeTaskType(condition.value),
        );
      }
      return evaluateOperator(rawVal, condition.operator, condition.value);
    });
  }

  return true;
}

export function evaluateDealFilter(
  deal: Deal,
  filter: AdvancedFilter | null | undefined,
  context?: FilterEvaluationContext,
): boolean {
  if (!filter) return true;

  const validAndConditions = (filter.andConditions || []).filter(
    (c) => c.fieldId && c.operator,
  );
  const validOrConditions = (filter.orConditions || []).filter(
    (c) => c.fieldId && c.operator,
  );

  // Se não houver nenhuma condição configurada, passa
  if (validAndConditions.length === 0 && validOrConditions.length === 0) {
    return true;
  }

  // 1. Bloco TODAS (AND): Todas as condições deste bloco DEVEM ser verdadeiras
  if (validAndConditions.length > 0) {
    const allAndPassed = validAndConditions.every((condition) =>
      evaluateConditionForDeal(deal, condition, context),
    );
    if (!allAndPassed) return false;
  }

  // 2. Bloco QUALQUER (OR): Pelo menos UMA condição deste bloco DEVE ser verdadeira
  if (validOrConditions.length > 0) {
    const anyOrPassed = validOrConditions.some((condition) =>
      evaluateConditionForDeal(deal, condition, context),
    );
    if (!anyOrPassed) return false;
  }

  return true;
}

export interface TaskFilterEvaluationContext {
  dealsMap?: Record<string, Deal>;
  companiesMap?: Record<string, Company>;
  contactsMap?: Record<string, Contact>;
  salesUsersMap?: Record<string, { id: string | number; name: string }>;
}

export function evaluateConditionForTask(
  task: Task,
  condition: FilterCondition,
  context?: TaskFilterEvaluationContext,
): boolean {
  if (!condition.fieldId) return true;

  // 1. Condição na própria Atividade
  if (condition.entity === "activity") {
    let rawVal: any;
    const fId = condition.fieldId;
    if (fId === "title" || fId === "text" || fId === "subject") {
      rawVal = task.text;
    } else if (fId === "type") {
      rawVal = task.type;
    } else if (fId === "priority" || fId === "prioridade") {
      rawVal = task.prioridade;
    } else if (fId === "done") {
      rawVal = Boolean(task.done_date);
    } else if (fId === "due_date" || fId === "data_inicio") {
      rawVal = task.due_date || task.data_inicio;
    } else if (fId === "completed_at" || fId === "done_date") {
      rawVal = task.done_date;
    } else if (fId === "assigned_to" || fId === "sales_id") {
      rawVal = task.sales_id;
    } else if (fId === "deal_id") {
      rawVal = task.deal_id;
    } else if (fId === "contact_id") {
      rawVal = task.contact_id;
    } else if (fId === "company_id") {
      rawVal = task.company_id;
    } else if (fId === "description" || fId === "notes") {
      rawVal = task.notes;
    } else {
      rawVal = (task as any)[fId];
    }

    if (fId === "type") {
      return evaluateOperator(
        normalizeTaskType(rawVal),
        condition.operator,
        normalizeTaskType(condition.value),
      );
    }

    return evaluateOperator(rawVal, condition.operator, condition.value);
  }

  // 2. Condição no Negócio (Deal) vinculado
  if (condition.entity === "deal") {
    if (!task.deal_id) {
      return condition.operator === "esta vazio";
    }
    const deal = context?.dealsMap?.[String(task.deal_id)];
    if (!deal) {
      return false;
    }
    const rawVal = extractFieldValue(deal, condition.fieldId, context as any);
    if (condition.fieldId === "status") {
      return evaluateOperator(
        normalizeStatus(rawVal),
        condition.operator,
        normalizeStatus(condition.value),
      );
    }
    return evaluateOperator(rawVal, condition.operator, condition.value);
  }

  // 3. Condição na Organização (Company)
  if (condition.entity === "company") {
    const deal = task.deal_id
      ? context?.dealsMap?.[String(task.deal_id)]
      : null;
    const companyId = task.company_id || deal?.company_id;
    if (!companyId) {
      return condition.operator === "esta vazio";
    }
    const company = context?.companiesMap?.[String(companyId)];
    if (!company) return false;
    const rawVal = extractFieldValue(company, condition.fieldId);
    return evaluateOperator(rawVal, condition.operator, condition.value);
  }

  // 4. Condição no Contato (Contact / Pessoa)
  if (condition.entity === "contact") {
    const deal = task.deal_id
      ? context?.dealsMap?.[String(task.deal_id)]
      : null;
    const contactIds = task.contact_id
      ? [task.contact_id]
      : deal?.contact_ids || [];
    if (contactIds.length === 0) {
      return condition.operator === "esta vazio";
    }
    const matchingContacts = contactIds
      .map((id) => context?.contactsMap?.[String(id)])
      .filter(Boolean);
    if (matchingContacts.length === 0) return false;
    return matchingContacts.some((contact) => {
      const rawVal = extractFieldValue(contact, condition.fieldId);
      return evaluateOperator(rawVal, condition.operator, condition.value);
    });
  }

  return true;
}

export function evaluateTaskFilter(
  task: Task,
  filter: AdvancedFilter | null | undefined,
  context?: TaskFilterEvaluationContext,
): boolean {
  if (!filter) return true;

  const validAndConditions = (filter.andConditions || []).filter(
    (c) => c.fieldId && c.operator,
  );
  const validOrConditions = (filter.orConditions || []).filter(
    (c) => c.fieldId && c.operator,
  );

  if (validAndConditions.length === 0 && validOrConditions.length === 0) {
    return true;
  }

  if (validAndConditions.length > 0) {
    const allAndPassed = validAndConditions.every((condition) =>
      evaluateConditionForTask(task, condition, context),
    );
    if (!allAndPassed) return false;
  }

  if (validOrConditions.length > 0) {
    const anyOrPassed = validOrConditions.some((condition) =>
      evaluateConditionForTask(task, condition, context),
    );
    if (!anyOrPassed) return false;
  }

  return true;
}
